'use client'

export const dynamic = 'force-dynamic'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import {
  Briefcase, Check, Flag, GraduationCap, HandHeart, Hammer, HeartPulse, Inbox, Landmark, Laptop, Loader2,
  MapPin, Navigation, Search, Send, ShieldOff, Users, X,
} from 'lucide-react'

/* Masjid Connect — "Find help" discovery. Reads /api/connect/search (card fields only; NO
   contact). Whole surface 404s while masjidConnect is off (middleware FLAG_ROUTES /connect).
   Framing is khidmah, not advertising: find someone in your community who can help. */

const NAVY = '#0B132B', GREEN = '#0E7C61', TEAL = '#14B8A6', GOLD = '#D4AF37', SLATE = '#5b6472', LINE = '#e6e4dc', BEIGE = '#F6F4EE'

const CATEGORIES: Array<{ value: string; label: string; Icon: typeof HeartPulse }> = [
  { value: 'medical', label: 'Medical', Icon: HeartPulse },
  { value: 'legal', label: 'Legal', Icon: Landmark },
  { value: 'trades', label: 'Trades', Icon: Hammer },
  { value: 'education', label: 'Education', Icon: GraduationCap },
  { value: 'finance', label: 'Finance', Icon: Briefcase },
  { value: 'it', label: 'IT & digital', Icon: Laptop },
  { value: 'other', label: 'Other', Icon: HandHeart },
]
const catLabel = (v: string) => CATEGORIES.find((c) => c.value === v)?.label || v

type Relationship = 'none' | 'pending_sent' | 'pending_received' | 'connected'
type Row = {
  id: string; name: string; photoUrl: string | null; category: string; skillTags: string[]
  profession?: string | null; offers?: string[]
  blurb: string | null; masjidId: string | null; masjidName: string | null
  masjidLocality: string | null; masjidCity: string | null; distanceKm: number | null; visibility: string
  relationship?: Relationship // viewer↔profile state (server-driven); absent (→ none) when signed out
}
type Masjid = { id: string; name: string; city: string }

export default function ConnectFindPage() {
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')
  const [masjid, setMasjid] = useState<Masjid | null>(null)
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [geoState, setGeoState] = useState<'idle' | 'locating' | 'denied'>('idle')

  const [rows, setRows] = useState<Row[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [detail, setDetail] = useState<Row | null>(null)
  const [requestFor, setRequestFor] = useState<Row | null>(null)
  const [reportFor, setReportFor] = useState<Row | null>(null)

  // Block a profile's owner, then drop it from the current results so it vanishes immediately.
  async function block(r: Row) {
    if (!confirm(`Block ${r.name}? You won’t see each other on Masjid Connect.`)) return
    try {
      const res = await fetch('/api/connect/blocks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ talentProfileId: r.id }) })
      if (res.ok) { setRows((rs) => rs.filter((x) => x.id !== r.id)); setDetail(null) }
    } catch { /* ignore — user can retry */ }
  }

  // Withdraw a pending_sent request → server deletes the open row; optimistically return the card to
  // 'none' so it can be requested again. Requester-only (server-scoped to the session).
  async function withdraw(r: Row) {
    try {
      const res = await fetch('/api/connect/requests/withdraw', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ talentProfileId: r.id }) })
      if (res.ok) {
        setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, relationship: 'none' } : x)))
        setDetail((d) => (d && d.id === r.id ? { ...d, relationship: 'none' } : d))
      }
    } catch { /* ignore — user can retry */ }
  }

  const debouncedQ = useDebounced(q, 350)

  const search = useCallback(async () => {
    setStatus('loading')
    const p = new URLSearchParams()
    if (category) p.set('category', category)
    if (debouncedQ) p.set('q', debouncedQ)
    if (masjid) p.set('masjidId', masjid.id)
    if (coords) { p.set('lat', String(coords.lat)); p.set('lng', String(coords.lng)) }
    try {
      const res = await fetch(`/api/connect/search?${p.toString()}`)
      if (!res.ok) return setStatus('error')
      const data = await res.json()
      setRows(Array.isArray(data.results) ? data.results : [])
      setStatus('ready')
    } catch { setStatus('error') }
  }, [category, debouncedQ, masjid, coords])
  useEffect(() => { void search() }, [search])

  function useMyLocation() {
    if (!navigator.geolocation) { setGeoState('denied'); return }
    setGeoState('locating')
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoState('idle') },
      () => setGeoState('denied'),
      { timeout: 8000 },
    )
  }

  return (
    <main style={{ maxWidth: 1000, margin: '0 auto', padding: '30px 16px 72px' }}>
      {/* Intro — khidmah framing */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 22 }}>
        <div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: TEAL }}><Users size={14} /> Masjid Connect</span>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: NAVY, margin: '8px 0 6px', lineHeight: 1.15 }}>Find someone in your community who can help</h1>
          <p style={{ color: SLATE, fontSize: 15, lineHeight: 1.6, maxWidth: 640, margin: 0 }}>
            Neighbours who’ve offered their skills as khidmah — a doctor, teacher, or tradesman who prays beside you.
            Browse by need; reach out only with their permission.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 'none' }}>
          <Link href="/connect/profile" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: TEAL, color: '#fff', borderRadius: 10, padding: '10px 16px', fontSize: 13.5, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}><Users size={15} /> Offer your help</Link>
          <Link href="/connect/requests" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, border: `1.5px solid ${LINE}`, background: '#fff', color: NAVY, borderRadius: 10, padding: '9px 14px', fontSize: 13.5, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}><Inbox size={15} /> Requests</Link>
        </div>
      </div>

      {/* Trust / value explainer — honest, matches real behavior: browse shows card fields only; contact
          is request-gated (visibility 'on_request') and revealed ONLY on approval. Copy/UI only. */}
      <div style={{ background: '#f5faf8', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
        <div><div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5 }}>What you share</div><div style={{ color: SLATE, fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>Your name, profession, city and what you offer are visible to the community browsing Connect.</div></div>
        <div><div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5 }}>Your contact stays private</div><div style={{ color: SLATE, fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>Your phone or WhatsApp is never shown publicly — it’s shared only when you approve a request.</div></div>
        <div><div style={{ fontWeight: 700, color: NAVY, fontSize: 13.5 }}>You stay in control</div><div style={{ color: SLATE, fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>Approve a request and that person can reach you. Decline and nothing is shared.</div></div>
      </div>

      {/* Filter bar */}
      <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: 16, marginBottom: 22, boxShadow: '0 1px 2px rgba(0,0,0,.03)' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 260px', border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '0 12px' }}>
            <Search size={15} style={{ color: SLATE }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a skill or name…" style={{ border: 'none', outline: 'none', padding: '11px 0', fontSize: 14, flex: 1, fontFamily: 'inherit', background: 'transparent' }} />
          </div>
          <MasjidFilter value={masjid} onPick={setMasjid} />
          <button type="button" onClick={useMyLocation} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: `1.5px solid ${coords ? GREEN : LINE}`, background: coords ? '#f5faf8' : '#fff', color: coords ? GREEN : NAVY, borderRadius: 10, padding: '10px 14px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {geoState === 'locating' ? <Loader2 className="spin" size={15} /> : <Navigation size={15} />}
            {coords ? 'Sorting by distance' : geoState === 'denied' ? 'Location off' : 'Use my location'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Chip active={category === ''} onClick={() => setCategory('')} label="All" />
          {CATEGORIES.map((c) => <Chip key={c.value} active={category === c.value} onClick={() => setCategory(c.value === category ? '' : c.value)} label={c.label} Icon={c.Icon} />)}
        </div>
      </div>

      {/* Results */}
      {status === 'loading' ? (
        <div style={{ padding: 60, textAlign: 'center', color: SLATE }}><Loader2 className="spin" size={22} /></div>
      ) : status === 'error' ? (
        <Empty title="Search is unavailable right now" body="Please try again in a moment." retry={search} />
      ) : rows.length === 0 ? (
        <Empty
          title="No one’s listed help in this area yet"
          body="Masjid Connect is new. Be the first to offer your skills so your neighbours can find you."
          cta={{ href: '/connect/profile', label: 'Offer your help' }}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {rows.map((r) => <ProfileCard key={r.id} r={r} onOpen={() => setDetail(r)} onRequest={() => setRequestFor(r)} onWithdraw={() => void withdraw(r)} />)}
        </div>
      )}

      {detail && <DetailModal r={detail} onClose={() => setDetail(null)} onRequest={() => { setRequestFor(detail); setDetail(null) }} onWithdraw={() => void withdraw(detail)} onReport={() => { setReportFor(detail); setDetail(null) }} onBlock={() => void block(detail)} />}
      {requestFor && <RequestModal row={requestFor} onClose={() => setRequestFor(null)} />}
      {reportFor && <ReportModal row={reportFor} onClose={() => setReportFor(null)} />}
      <style jsx global>{`.spin{animation:cxspin 1s linear infinite}@keyframes cxspin{to{transform:rotate(360deg)}}`}</style>
    </main>
  )
}

function ProfileCard({ r, onOpen, onRequest, onWithdraw }: { r: Row; onOpen: () => void; onRequest: () => void; onWithdraw: () => void }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <Avatar name={r.name} photoUrl={r.photoUrl} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
          {/* Real profession when the user shared it, else the coarse category (never empty). */}
          <span style={{ display: 'inline-block', marginTop: 3, fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: TEAL, background: 'rgba(20,184,166,.1)', borderRadius: 999, padding: '2px 9px' }}>{r.profession || catLabel(r.category)}</span>
        </div>
      </div>
      {r.blurb && <p style={{ fontSize: 13, color: SLATE, lineHeight: 1.55, margin: '0 0 12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.blurb}</p>}
      {r.skillTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {r.skillTags.slice(0, 4).map((t) => <span key={t} style={{ fontSize: 11.5, color: GREEN, background: '#eef6f3', border: '1px solid #d7e9e2', borderRadius: 999, padding: '3px 9px' }}>{t}</span>)}
          {r.skillTags.length > 4 && <span style={{ fontSize: 11.5, color: SLATE, padding: '3px 4px' }}>+{r.skillTags.length - 4}</span>}
        </div>
      )}
      {/* Offers to help with (contribution types the user chose) — only when present. */}
      {r.offers && r.offers.length > 0 && (
        <div style={{ fontSize: 12, color: SLATE, marginBottom: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <strong style={{ color: NAVY, fontWeight: 700 }}>Offers:</strong> {r.offers.slice(0, 3).join(', ')}{r.offers.length > 3 ? '…' : ''}
        </div>
      )}
      <div style={{ marginTop: 'auto' }}>
        {/* Location — the person's masjid city + masjid (the vouching/followed-masjid proxy). */}
        {(r.masjidCity || r.masjidName || r.distanceKm != null) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: SLATE, marginBottom: 12 }}>
            <MapPin size={13} style={{ color: GREEN, flex: 'none' }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[r.masjidCity, r.masjidName].filter(Boolean).join(' · ') || 'Community'}
              {r.distanceKm != null ? `  ·  ${r.distanceKm} km` : ''}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onOpen} style={{ flex: 1, background: BEIGE, color: NAVY, border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>View</button>
          <RequestButton onClick={onRequest} onWithdraw={onWithdraw} relationship={r.relationship} />
        </div>
      </div>
    </div>
  )
}

/* State-aware connect control (LinkedIn-style), driven by the server `relationship` field — no longer
   a bare green button that leans on the 409. The actual contact reveal is still gated behind approval.
     none            → "Request to connect" (opens the modal)
     pending_sent    → "Requested" (neutral, disabled — already asked)
     pending_received→ "Respond" (→ the requests inbox to accept/decline)
     connected       → "Connected" (neutral, done) */
function RequestButton({ onClick, onWithdraw, full = false, relationship = 'none' }: { onClick: () => void; onWithdraw?: () => void; full?: boolean; relationship?: Relationship }) {
  const base: React.CSSProperties = { flex: full ? 1 : undefined, borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }
  if (relationship === 'pending_sent') {
    // Requested → tappable to WITHDRAW (cancel the pending request → back to 'none').
    return <button type="button" onClick={onWithdraw} title="Withdraw your request" style={{ ...base, background: '#fff', color: SLATE, border: `1px solid ${LINE}`, cursor: onWithdraw ? 'pointer' : 'default' }}>Requested · Withdraw</button>
  }
  if (relationship === 'connected') {
    return <button type="button" disabled style={{ ...base, background: '#eef6f3', color: GREEN, border: '1px solid #d7e9e2', cursor: 'default' }}>Connected ✓</button>
  }
  if (relationship === 'pending_received') {
    return <a href="/connect/requests" style={{ ...base, background: NAVY, color: '#fff', border: 'none', cursor: 'pointer', textAlign: 'center', textDecoration: 'none' }}>Respond</a>
  }
  return (
    <button type="button" onClick={onClick} style={{ ...base, background: GREEN, color: '#fff', border: 'none', cursor: 'pointer' }}>
      Request to connect
    </button>
  )
}

/* The request modal — optional message → POST /api/connect/requests. Handles the honest
   response states: 201 pending, 409 duplicate, 429 rate-limited, 403 blocked, 401 sign-in. */
function RequestModal({ row, onClose }: { row: Row; onClose: () => void }) {
  const [message, setMessage] = useState('')
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form')
  const [err, setErr] = useState('')

  async function send() {
    setPhase('sending'); setErr('')
    try {
      const res = await fetch('/api/connect/requests', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ talentProfileId: row.id, message: message.trim() || undefined }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) { setPhase('done'); return }
      if (res.status === 401) { setErr('Please sign in to send a request.'); setPhase('form'); return }
      // 409 duplicate / 429 rate-limit / 403 block / others — surface the server's own message.
      setErr(data.error || 'Could not send your request. Please try again.'); setPhase('form')
    } catch { setErr('Could not send your request — please retry.'); setPhase('form') }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,19,43,.5)', display: 'grid', placeItems: 'center', padding: 16, zIndex: 70 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 420, width: '100%', padding: 24, position: 'relative' }}>
        <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: BEIGE, border: 'none', borderRadius: 999, width: 30, height: 30, display: 'grid', placeItems: 'center', cursor: 'pointer', color: SLATE }}><X size={16} /></button>
        {phase === 'done' ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: '#eaf7f1', display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: GREEN }}><Check size={26} /></div>
            <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 6 }}>Request sent</div>
            <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: '0 0 18px' }}>{row.name} will be notified. If they approve, you’ll be able to view their contact details. You can track this under <strong>Requests</strong>.</p>
            <Link href="/connect/requests" style={{ display: 'inline-block', background: GREEN, color: '#fff', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>View my requests</Link>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 4 }}>Request to connect</div>
            <p style={{ fontSize: 13.5, color: SLATE, lineHeight: 1.55, margin: '0 0 16px' }}>Send {row.name} a short note. Your own contact stays private — they’ll only see your name, and share their details only if they approve.</p>
            <textarea value={message} onChange={(e) => setMessage(e.target.value.slice(0, 300))} rows={4} placeholder="e.g. Assalamu alaikum — I’m looking for help with…" style={{ width: '100%', border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
            <div style={{ fontSize: 11.5, color: SLATE, textAlign: 'right', marginTop: 4 }}>{message.length}/300</div>
            {err && <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 9, padding: '10px 12px', fontSize: 13, lineHeight: 1.5, margin: '6px 0 0' }}>{err}</div>}
            <button type="button" onClick={() => void send()} disabled={phase === 'sending'} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', marginTop: 14, opacity: phase === 'sending' ? 0.6 : 1 }}>
              {phase === 'sending' ? <Loader2 className="spin" size={16} /> : <Send size={16} />} Send request
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// Expanded-card DETAIL as a modal (not a separate route) — shows the fuller blurb + all tags,
// still NO contact. Reads only the already-fetched card row; no extra fetch, no reveal path.
function DetailModal({ r, onClose, onRequest, onWithdraw, onReport, onBlock }: { r: Row; onClose: () => void; onRequest: () => void; onWithdraw: () => void; onReport: () => void; onBlock: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,19,43,.5)', display: 'grid', placeItems: 'center', padding: 16, zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 460, width: '100%', padding: 24, position: 'relative', maxHeight: '86vh', overflowY: 'auto' }}>
        <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: BEIGE, border: 'none', borderRadius: 999, width: 30, height: 30, display: 'grid', placeItems: 'center', cursor: 'pointer', color: SLATE }}><X size={16} /></button>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
          <Avatar name={r.name} photoUrl={r.photoUrl} size={56} />
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: NAVY }}>{r.name}</div>
            <span style={{ display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: TEAL, background: 'rgba(20,184,166,.1)', borderRadius: 999, padding: '3px 10px' }}>{catLabel(r.category)}</span>
          </div>
        </div>
        {r.blurb && <p style={{ fontSize: 14.5, color: '#333', lineHeight: 1.65, margin: '0 0 16px' }}>{r.blurb}</p>}
        {r.skillTags.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: SLATE, marginBottom: 7 }}>Skills</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {r.skillTags.map((t) => <span key={t} style={{ fontSize: 12.5, color: GREEN, background: '#eef6f3', border: '1px solid #d7e9e2', borderRadius: 999, padding: '4px 11px' }}>{t}</span>)}
            </div>
          </div>
        )}
        {r.masjidName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: SLATE, marginBottom: 20, background: BEIGE, borderRadius: 10, padding: '11px 13px' }}>
            <MapPin size={15} style={{ color: GREEN }} />
            <span>Vouched at <strong style={{ color: NAVY }}>{r.masjidName}</strong>{r.masjidLocality ? ` · ${r.masjidLocality}` : r.masjidCity ? ` · ${r.masjidCity}` : ''}{r.distanceKm != null ? `  ·  ${r.distanceKm} km away` : ''}</span>
          </div>
        )}
        <RequestButton onClick={onRequest} onWithdraw={onWithdraw} full relationship={r.relationship} />
        <p style={{ fontSize: 12, color: SLATE, textAlign: 'center', margin: '10px 0 0', lineHeight: 1.5 }}>Their contact details stay private — shared only if they approve your request.</p>
        {/* Moderation affordances — quiet, secondary. */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${LINE}` }}>
          <button type="button" onClick={onReport} style={modLink}><Flag size={13} /> Report</button>
          <button type="button" onClick={onBlock} style={modLink}><ShieldOff size={13} /> Block</button>
        </div>
      </div>
    </div>
  )
}

/* Report a profile → POST /api/connect/reports. Honest 201 / 409 (already reported) / 400. */
function ReportModal({ row, onClose }: { row: Row; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form')
  const [err, setErr] = useState('')

  async function send() {
    if (!reason.trim()) { setErr('Please describe the problem.'); return }
    setPhase('sending'); setErr('')
    try {
      const res = await fetch('/api/connect/reports', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ talentProfileId: row.id, reason: reason.trim() }) })
      const data = await res.json().catch(() => ({}))
      if (res.ok) { setPhase('done'); return }
      setErr(data.error || 'Could not submit your report.'); setPhase('form')
    } catch { setErr('Could not submit your report — please retry.'); setPhase('form') }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,19,43,.5)', display: 'grid', placeItems: 'center', padding: 16, zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, maxWidth: 420, width: '100%', padding: 24, position: 'relative' }}>
        <button type="button" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: BEIGE, border: 'none', borderRadius: 999, width: 30, height: 30, display: 'grid', placeItems: 'center', cursor: 'pointer', color: SLATE }}><X size={16} /></button>
        {phase === 'done' ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: BEIGE, display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: GREEN }}><Check size={26} /></div>
            <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 6 }}>Report submitted</div>
            <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: '0 0 18px' }}>Thank you. Our team will review this profile.</p>
            <button type="button" onClick={onClose} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: NAVY, marginBottom: 4 }}>Report {row.name}</div>
            <p style={{ fontSize: 13.5, color: SLATE, lineHeight: 1.55, margin: '0 0 14px' }}>Tell us what’s wrong with this profile. Reports are private and reviewed by our team.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))} rows={4} placeholder="e.g. Impersonation, offensive content, wrong information…" style={{ width: '100%', border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
            <div style={{ fontSize: 11.5, color: SLATE, textAlign: 'right', marginTop: 4 }}>{reason.length}/500</div>
            {err && <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 9, padding: '10px 12px', fontSize: 13, margin: '6px 0 0' }}>{err}</div>}
            <button type="button" onClick={() => void send()} disabled={phase === 'sending'} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', marginTop: 14, opacity: phase === 'sending' ? 0.6 : 1 }}>
              {phase === 'sending' ? <Loader2 className="spin" size={16} /> : <Flag size={16} />} Submit report
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function Avatar({ name, photoUrl, size = 46 }: { name: string; photoUrl: string | null; size?: number }) {
  const initials = name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || 'U'
  if (photoUrl) return <img src={photoUrl} alt="" width={size} height={size} style={{ width: size, height: size, borderRadius: 12, objectFit: 'cover', flex: 'none' }} />
  return <div style={{ width: size, height: size, borderRadius: 12, background: GREEN, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: size * 0.36, flex: 'none' }}>{initials}</div>
}

function Chip({ active, onClick, label, Icon }: { active: boolean; onClick: () => void; label: string; Icon?: typeof HeartPulse }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1.5px solid ${active ? GREEN : LINE}`, background: active ? GREEN : '#fff', color: active ? '#fff' : NAVY, borderRadius: 999, padding: '7px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
      {Icon && <Icon size={13} />} {label}
    </button>
  )
}

function MasjidFilter({ value, onPick }: { value: Masjid | null; onPick: (m: Masjid | null) => void }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Masjid[]>([])
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return }
    let alive = true
    const id = setTimeout(() => {
      fetch(`/api/masjids?q=${encodeURIComponent(q.trim())}&limit=6`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (alive) setResults((d?.masjids || []).map((m: any) => ({ id: String(m.id), name: String(m.nameEnglish || m.name || 'Masjid'), city: String(m.city || '') }))) })
        .catch(() => { if (alive) setResults([]) })
    }, 300)
    return () => { alive = false; clearTimeout(id) }
  }, [q])

  if (value) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, border: `1.5px solid ${GREEN}`, background: '#f5faf8', borderRadius: 10, padding: '9px 12px', flex: '1 1 220px' }}>
      <MapPin size={14} style={{ color: GREEN }} />
      <span style={{ flex: 1, fontSize: 13.5, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.name}</span>
      <button type="button" aria-label="Clear masjid filter" onClick={() => onPick(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: SLATE, display: 'inline-flex' }}><X size={14} /></button>
    </div>
  )
  return (
    <div style={{ position: 'relative', flex: '1 1 220px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${LINE}`, borderRadius: 10, padding: '0 12px' }}>
        <MapPin size={15} style={{ color: SLATE }} />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} placeholder="Filter by masjid…" style={{ border: 'none', outline: 'none', padding: '11px 0', fontSize: 14, flex: 1, fontFamily: 'inherit', background: 'transparent' }} />
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.1)', zIndex: 30, overflow: 'hidden' }}>
          {results.map((m) => (
            <button key={m.id} type="button" onClick={() => { onPick(m); setOpen(false); setQ('') }} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', borderBottom: `1px solid ${LINE}`, cursor: 'pointer' }}>
              <MapPin size={13} style={{ color: GREEN, flex: 'none' }} />
              <span style={{ fontSize: 13, color: NAVY }}>{m.name}{m.city ? <span style={{ color: SLATE }}> · {m.city}</span> : null}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Empty({ title, body, cta, retry }: { title: string; body: string; cta?: { href: string; label: string }; retry?: () => void }) {
  return (
    <div style={{ textAlign: 'center', border: `1px dashed ${LINE}`, borderRadius: 18, padding: '52px 24px', background: '#fff' }}>
      <div style={{ width: 52, height: 52, borderRadius: 16, background: BEIGE, display: 'grid', placeItems: 'center', margin: '0 auto 14px', color: GREEN }}><Users size={26} /></div>
      <div style={{ fontSize: 17, fontWeight: 700, color: NAVY, marginBottom: 7 }}>{title}</div>
      <p style={{ fontSize: 14, color: SLATE, maxWidth: 400, margin: '0 auto 18px', lineHeight: 1.6 }}>{body}</p>
      {cta && <Link href={cta.href} style={{ display: 'inline-block', background: GREEN, color: '#fff', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>{cta.label}</Link>}
      {retry && <button type="button" onClick={retry} style={{ display: 'inline-block', background: GREEN, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Retry</button>}
    </div>
  )
}

const modLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: SLATE, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  const ref = useRef(value)
  ref.current = value
  useEffect(() => { const id = setTimeout(() => setV(ref.current), ms); return () => clearTimeout(id) }, [value, ms])
  return v
}
